import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { createLogger } from '../utils/logger';

const log = createLogger('API');
// Empty string routes requests through the Vite dev proxy (/api → localhost:3002).
// In production, set VITE_API_BASE_URL to the absolute backend URL.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add subdomain in header for public backend
    // Public backend uses subdomain to determine customer
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      let subdomain: string | null = null;
      
      // Extract subdomain from hostname (e.g., "mysubdomain.localhost" or "mysubdomain.example.com")
      if (hostname.includes('.localhost') || (hostname !== 'localhost' && !hostname.startsWith('127.0.0.1'))) {
        const parts = hostname.split('.');
        if (parts.length > 1 && parts[0] !== 'localhost' && parts[0] !== 'www') {
          subdomain = parts[0];
        }
      }
      
      // For localhost development without subdomain, use a default subdomain from env or 'test'
      // This allows testing the public-frontend on localhost without needing to set up subdomain routing
      // Note: The subdomain must exist in the database (customer with matching subdomain)
      if (!subdomain && (hostname === 'localhost' || hostname.startsWith('127.0.0.1'))) {
        const defaultSubdomain = import.meta.env.VITE_DEFAULT_SUBDOMAIN || 'test';
        subdomain = defaultSubdomain;
        log.debug(`Using default subdomain for localhost: ${subdomain}`);
      }
      
      if (subdomain && config.headers) {
        config.headers['X-Subdomain'] = subdomain;
      }
    }
    
    return config;
  },
  (error: AxiosError) => {
    log.error('Request error:', error.message);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    // Public endpoints don't require auth, so no 401 handling needed
    return Promise.reject(error);
  }
);

export default apiClient;

